import React, { useState, useRef, useEffect } from "react";
import { IoChevronDown } from "react-icons/io5";
import { useTabs } from "contexts/TabContext";
import { useDealData } from "contexts/DealContext";

const FacilitySelector = ({
    facilities = [],
    selectedFacility,
    setSelectedFacility,
    placeholder = "Select a facility",
    size = "medium",
    isDisabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const optionsRef = useRef([]);
    const [displayValue, setDisplayValue] = useState(placeholder);
    const { maintainingTab } = useTabs();
    const { isDealLoading } = useDealData();

    // Check if the selector should be disabled during tab maintenance or due to prop
    const effectivelyDisabled = isDisabled || (maintainingTab && isDealLoading);

    // Update display value when selectedFacility changes
    useEffect(() => {
        if (!selectedFacility) {
            setDisplayValue(placeholder);
            return;
        }
        setDisplayValue(selectedFacility);
    }, [selectedFacility, placeholder]);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Update refs array when options change
    useEffect(() => {
        optionsRef.current = optionsRef.current.slice(0, facilities.length);
    }, [facilities]);

    // Keyboard event handler
    const handleKeyDown = (e) => {
        if (effectivelyDisabled) return;

        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
                setActiveIndex(0);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prevIndex =>
                    prevIndex < facilities.length - 1 ? prevIndex + 1 : 0
                );
                break;

            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prevIndex =>
                    prevIndex > 0 ? prevIndex - 1 : facilities.length - 1
                );
                break;

            case 'Enter':
            case ' ':
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < facilities.length) {
                    handleSelect(facilities[activeIndex]);
                }
                break;

            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;

            case 'Tab':
                setIsOpen(false);
                break;

            case 'Home':
                e.preventDefault();
                setActiveIndex(0);
                break;

            case 'End':
                e.preventDefault();
                setActiveIndex(facilities.length - 1);
                break;

            default:
                const key = e.key.toLowerCase();
                if (key.length === 1 && /[a-z0-9]/.test(key)) {
                    const nextIndex = facilities.findIndex((facility, index) =>
                        index > activeIndex && facility.toLowerCase().startsWith(key)
                    );

                    if (nextIndex !== -1) {
                        setActiveIndex(nextIndex);
                    } else {
                        const firstIndex = facilities.findIndex((facility) =>
                            facility.toLowerCase().startsWith(key)
                        );

                        if (firstIndex !== -1) {
                            setActiveIndex(firstIndex);
                        }
                    }
                }
                break;
        }
    };

    // Scroll active option into view
    useEffect(() => {
        if (isOpen && activeIndex >= 0 && optionsRef.current[activeIndex]) {
            optionsRef.current[activeIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    }, [activeIndex, isOpen]);

    // Handler for selection changes
    const handleSelect = (selectedValue) => {
        if (effectivelyDisabled) return;
        setIsOpen(false);
        // If placeholder is selected, select "Consolidated"
        if (selectedValue === "") {
            setSelectedFacility("Consolidated");
        } else {
            setSelectedFacility(selectedValue);
        }
    };

    return (
        <div
            className="relative w-full sm:w-[150px] md:w-[150px] lg:min-w-[200px] xl:min-w-[250px]"
            ref={dropdownRef}
            onKeyDown={handleKeyDown}
            tabIndex={effectivelyDisabled ? "-1" : "0"}
        >
            <div
                className={`flex items-center justify-between px-3 py-2 bg-white border-b border-[#89949f] ${effectivelyDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${size === "small" ? "text-sm" : "text-base"} ${isOpen ? "border-blue-500" : ""}`}
                onClick={() => !effectivelyDisabled && setIsOpen(!isOpen)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls="facility-selector"
                role="combobox"
                aria-disabled={effectivelyDisabled}
            >
                <span className="truncate overflow-hidden">{displayValue}</span>
                {!effectivelyDisabled && <IoChevronDown className={`w-[12px] h-[12px] transition-transform ml-2 ${isOpen ? 'rotate-180' : ''}`} />}
            </div>

            {isOpen && !effectivelyDisabled && (
                <div
                    className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                    role="listbox"
                >
                    <ul className="py-1">
                        <li
                            ref={el => optionsRef.current[0] = el}
                            className={`px-4 py-2 font-medium text-gray-500 ${activeIndex === 0 ? "bg-blue-100" : "hover:bg-gray-100"} cursor-pointer`}
                            onClick={() => handleSelect("")}
                            onMouseEnter={() => setActiveIndex(0)}
                            role="option"
                            aria-selected={activeIndex === 0}
                            tabIndex="-1"
                        >
                            {placeholder}
                        </li>
                        {facilities.map((facility, index) => (
                            <li
                                key={facility}
                                ref={el => optionsRef.current[index + 1] = el}
                                className={`px-4 py-2 font-medium ${activeIndex === index + 1 ? "bg-blue-100" : "hover:bg-gray-100"} cursor-pointer`}
                                onClick={() => handleSelect(facility)}
                                onMouseEnter={() => setActiveIndex(index + 1)}
                                role="option"
                                aria-selected={activeIndex === index + 1}
                                tabIndex="-1"
                            >
                                {facility}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default FacilitySelector;